import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase, getUserProfile, handleSupabaseError } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const PRODUCTION_URL = 'https://leadgenpr6367.builtwithrocket.new';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const mounted = useRef(true);
  const initialized = useRef(false);
  const authListener = useRef(null);
  const activeFetchId = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeFetchId.current = null;
      try {
        authListener?.current?.subscription?.unsubscribe();
      } catch (e) {}
      authListener.current = null;
    };
  }, []);

  const loadUserProfile = useCallback(async (userId) => {
    if (!userId || !supabase) return null;
    const fetchId = Symbol('profile-fetch');
    activeFetchId.current = fetchId;
    try {
      if (mounted?.current) setProfileLoading(true);
      const profile = await getUserProfile(userId);
      if (mounted?.current && activeFetchId?.current === fetchId) {
        setUserProfile(profile || null);
        if (profile?.role === 'admin' || profile?.is_admin === true) {
          setIsAdmin(true);
        }
      }
      return profile;
    } catch (err) {
      console.error('Error loading user profile:', err);
      if (mounted?.current && activeFetchId?.current === fetchId) {
        setUserProfile(null);
      }
      return null;
    } finally {
      if (mounted?.current && activeFetchId?.current === fetchId) {
        setProfileLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (initialized?.current) return;
    initialized.current = true;

    if (!supabase) {
      if (mounted?.current) {
        setLoading(false);
        setIsInitialized(true);
      }
      return;
    }

    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        if (sessionError) console.error('Session init error:', sessionError);

        if (mounted?.current) {
          const currentUser = session?.user ?? null;
          setUser(currentUser);
          if (currentUser) {
            loadUserProfile(currentUser?.id)?.catch(() => {});
          }
        }

        if (mounted?.current) {
          const { data } = supabase?.auth?.onAuthStateChange(async (event, newSession) => {
            if (!mounted?.current) return;
            const newUser = newSession?.user ?? null;

            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              if (mounted?.current) {
                setUser(newUser);
                setError(null);
              }
              if (newUser) loadUserProfile(newUser?.id)?.catch(() => {});
            } else if (event === 'SIGNED_OUT') {
              if (mounted?.current) {
                setUser(null);
                setUserProfile(null);
                setIsAdmin(false);
                setError(null);
              }
            } else if (event === 'USER_UPDATED') {
              if (mounted?.current) setUser(newUser);
              if (newUser) loadUserProfile(newUser?.id)?.catch(() => {});
            } else if (event === 'PASSWORD_RECOVERY') {
              if (newUser && mounted?.current) {
                setUser(newUser);
              }
            } else if (event === 'INITIAL_SESSION') {
              // Already handled above via getSession
            } else {
              if (newUser) {
                if (mounted?.current) setUser(newUser);
                loadUserProfile(newUser?.id)?.catch(() => {});
              } else {
                if (mounted?.current) {
                  setUser(null);
                  setUserProfile(null);
                  setIsAdmin(false);
                }
              }
            }
          });
          authListener.current = data;
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        if (mounted?.current) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    initializeAuth();
  }, [loadUserProfile]);

  const signIn = useCallback(async (email, password) => {
    if (!supabase) return { success: false, error: 'Authentication service unavailable' };
    try {
      setError(null);
      const { data, error } = await supabase?.auth?.signInWithPassword({ email: email?.trim(), password });
      if (error) {
        let errorMessage = handleSupabaseError(error) || 'Failed to sign in';
        if (mounted?.current) setError(errorMessage);
        return { success: false, error: errorMessage };
      }
      if (data?.user && mounted?.current) {
        setUser(data?.user);
        setError(null);
        loadUserProfile(data?.user?.id)?.catch(() => {});
      }
      return { success: true, user: data?.user, session: data?.session };
    } catch (err) {
      console.error('Sign in error:', err);
      const msg = err?.message || '';
      const lowerMsg = msg?.toLowerCase();
      let errorMessage;
      if (
        lowerMsg?.includes('failed to fetch') ||
        lowerMsg?.includes('networkerror') ||
        lowerMsg?.includes('network') ||
        lowerMsg?.includes('authretryablefetcherror') ||
        err?.name === 'AuthRetryableFetchError'
      ) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMessage = msg || 'An unexpected error occurred';
      }
      if (mounted?.current) setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [loadUserProfile]);

  const signUp = useCallback(async (email, password, userData = {}) => {
    if (!supabase) return { success: false, error: 'Authentication service unavailable' };
    try {
      setError(null);
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${PRODUCTION_URL}/email-confirmation`
        }
      });
      if (error) {
        let errorMessage = handleSupabaseError(error) || 'Failed to sign up';
        if (mounted?.current) setError(errorMessage);
        return { success: false, error: errorMessage };
      }
      if (data?.user && !data?.session) {
        try { localStorage.setItem('pendingConfirmationEmail', email); } catch (e) {}
      }
      return {
        success: true,
        user: data?.user,
        session: data?.session,
        needsConfirmation: !data?.session
      };
    } catch (err) {
      console.error('Sign up error:', err);
      let errorMessage = err?.message || 'An unexpected error occurred';
      if (mounted?.current) setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return { success: false, error: 'Authentication service unavailable' };
    try {
      setError(null);
      if (mounted?.current) {
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
      }
      await supabase?.auth?.signOut();
      return { success: true };
    } catch (err) {
      console.error('Sign out error:', err);
      if (mounted?.current) {
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
      }
      return { success: true };
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    if (!supabase) return { success: false, error: 'Authentication service unavailable' };
    try {
      setError(null);
      const { error } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
        redirectTo: `${PRODUCTION_URL}/reset-password`
      });
      if (error) {
        let errorMessage = handleSupabaseError(error) || 'Failed to send reset email';
        if (mounted?.current) setError(errorMessage);
        return { success: false, error: errorMessage };
      }
      return { success: true };
    } catch (err) {
      console.error('Reset password error:', err);
      let errorMessage = err?.message || 'An unexpected error occurred';
      if (mounted?.current) setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const updateProfile = useCallback(async (updates = {}) => {
    if (!supabase || !user?.id) return { success: false, error: 'Not authenticated' };
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.upsert({
          user_id: user?.id,
          ...updates,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' });
      if (error) throw error;
      await loadUserProfile(user?.id);
      return { success: true };
    } catch (err) {
      console.error('Update profile error:', err);
      return { success: false, error: err?.message || 'Failed to update profile' };
    }
  }, [user?.id, loadUserProfile]);

  const value = React.useMemo(() => ({
    user,
    userProfile,
    loading,
    profileLoading,
    error,
    isInitialized,
    isAdmin,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    loadUserProfile,
    setError,
  }), [user, userProfile, loading, profileLoading, error, isInitialized, isAdmin, signIn, signUp, signOut, resetPassword, updateProfile, loadUserProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;